import React from 'react';
import { MapPinned } from 'lucide-react';
import { useAppSelector } from 'src/store';
import {
    getAppRoutesAvailability,
    getProjectRoutesAvailability,
    getSurveyRoutesAvailability,
    getMapRoutesAvailability,
} from 'src/routes/state/routesSelector';
import { useLocation } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { projectRouteSettings } from 'src/app/projects/Projects';
import { surveyRouteSettings } from 'src/app/surveys/Survey';
import { mapRouteSettings } from 'src/app/maps/Maps';
import { appRouteSettings } from 'src/routes/AppRoutes';
import useHabitatInsightsAccess from 'src/shared/hooks/useHabitatInsightsAccess';
import { isWithheld } from 'src/shared/utils/productAccess';
import { useIsWithheldFromUser, useIsSetToHidden } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import NestedSidebarMenuItem, { NestedSidebarMenuSubItem, isSubGroup } from './NestedSidebarMenuItem';
import useEcologyHubMenuItem from './useEcologyHubMenuItem';
import useTablesMenuItem from './useTablesMenuItem';

const NaturePerformanceMenu = () => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const projectRoutesAvailability = useAppSelector(getProjectRoutesAvailability);
    const surveyRoutesAvailability = useAppSelector(getSurveyRoutesAvailability);
    const mapRoutesAvailability = useAppSelector(getMapRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const ecologyHubItem = useEcologyHubMenuItem();
    const tablesItem = useTablesMenuItem();
    const isHabitatInsightsWithheld = isWithheld(useHabitatInsightsAccess());
    const isSpeciesInsightsWithheld = useIsWithheldFromUser(ProductKey.SPECIES_INSIGHTS);
    const isEdnaWithheld = useIsWithheldFromUser(ProductKey.EDNA);
    const isHabitatInsightsSetToHidden = useIsSetToHidden(ProductKey.HABITAT_INSIGHTS);
    const isSpeciesInsightsSetToHidden = useIsSetToHidden(ProductKey.SPECIES_INSIGHTS);
    const isEdnaSetToHidden = useIsSetToHidden(ProductKey.EDNA);

    if (!appRoutesAvailability.projects) {
        return null;
    }

    const subItems = (
        [
            projectRoutesAvailability.siteSummary && {
                title: 'Site Summary',
                testId: 'site-summary-menu-button',
                to: navigate.paths.siteSummary(),
                isActive: pathname.startsWith('/projects/site-summary'),
            },
            appRoutesAvailability.survey &&
                surveyRoutesAvailability.surveyDesignSummary && {
                    title: 'Survey design',
                    testId: 'survey-design-menu-button',
                    to: navigate.paths.surveyDesignSummary(),
                    isActive:
                        pathname === '/survey' + surveyRouteSettings.surveyDesignSummary.path ||
                        pathname + '/' === '/survey' + surveyRouteSettings.surveyDesignSummary.path,
                },
            appRoutesAvailability.map &&
                mapRoutesAvailability.speciesMap &&
                !isEdnaWithheld && {
                    title: mapRouteSettings.speciesMap.name,
                    testId: 'species-map-menu-item',
                    to: navigate.paths.speciesMap(),
                    isActive: pathname.includes('/map' + mapRouteSettings.speciesMap.path),
                    isHiddenFromCustomers: isEdnaSetToHidden,
                },
            appRoutesAvailability.map &&
                mapRoutesAvailability.habitatMap &&
                !isHabitatInsightsWithheld && {
                    title: mapRouteSettings.habitatMap.name,
                    testId: 'habitat-map-menu-item',
                    to: navigate.paths.habitatMap(),
                    isActive: pathname.includes('/map' + mapRouteSettings.habitatMap.path),
                    isHiddenFromCustomers: isHabitatInsightsSetToHidden,
                },
            projectRoutesAvailability.freemiumSpeciesInsights &&
                !isSpeciesInsightsWithheld && {
                    title: projectRouteSettings.freemiumSpeciesInsights.name,
                    testId: 'species-insights-menu-item',
                    to: navigate.paths.freemiumSpeciesInsights(),
                    isActive:
                        pathname === '/projects' + projectRouteSettings.freemiumSpeciesInsights.path ||
                        pathname + '/' === '/projects' + projectRouteSettings.freemiumSpeciesInsights.path,
                    isHiddenFromCustomers: isSpeciesInsightsSetToHidden,
                },
            appRoutesAvailability.dashboard &&
                !isSpeciesInsightsWithheld && {
                    title: 'Dashboard',
                    testId: 'dashboard-menu-button',
                    to: navigate.paths.dashboard(),
                    isActive: pathname.startsWith(appRouteSettings.dashboard.path),
                    isHiddenFromCustomers: isSpeciesInsightsSetToHidden,
                },
            ecologyHubItem,
            projectRoutesAvailability.siteFiles && {
                title: 'Files',
                testId: 'files-menu-button',
                to: navigate.paths.siteFiles(),
                isActive: pathname.startsWith('/projects/site-files'),
            },
            tablesItem,
        ] as (NestedSidebarMenuSubItem | false | null)[]
    ).filter((item): item is NestedSidebarMenuSubItem => Boolean(item));

    if (subItems.length === 0) {
        return null;
    }

    const isActive = subItems.some(item => (isSubGroup(item) ? item.subItems.some(subItem => subItem.isActive) : item.isActive));

    return (
        <NestedSidebarMenuItem
            label='Nature Performance'
            testId='nature-performance-menu-group-button'
            icon={<MapPinned />}
            isActive={isActive}
            defaultOpen={isActive}
            subItems={subItems}
        />
    );
};

export default NaturePerformanceMenu;
